import PropTypes, { bool, string } from "prop-types";
import React, { cloneElement, useContext } from "react";
import { Prompt } from "react-router-dom";
import { MdModeEdit } from "react-icons/md";

import Tabs, { TabPane } from "rc-tabs";
import TabContent from "rc-tabs/lib/TabContent";
import { filterTabsByAcl } from "src/utils/RoleBasedSecurity/filters";
import RbsContext from "src/utils/RoleBasedSecurity/RbsContext";
import __ from "src/utils/Translations";
import UnsavedChangesModal from "src/Components/Modals/UnsavedChangesModal";
import { useEditedTabs } from "src/utils/hooks/useEditedTabs/useEditedTabs";
import { useProtectedTabNavigation } from "src/utils/hooks/useProtectedTabsNavigation/useProtectedTabsNavigation";
import { getModalMessage } from "src/utils/TabsMessage/tabsMessages";
import DefaultHashTabBar from "./DefaultHashTabBar";

const PROTECTED_TABS = ["employee_employment", "organization_structure"];

export default function TabsWithMemory({
  tabsConfig = [],
  activeKey,
  defaultActiveKey,
  animated = false,
  promptWhenUnsaved = true,
}) {
  const { userInfo } = useContext(RbsContext);
  const currentKey = activeKey || defaultActiveKey;

  const { editedTabs, setTabIsEdited, clearEditedTab, unsavedForms } =
    useEditedTabs();

  const {
    displayedKey,
    showModal,
    modalMessage,
    handleConfirmLeave,
    handleCancelLeave,
  } = useProtectedTabNavigation({
    currentKey,
    editedTabs,
    protectedTabs: PROTECTED_TABS,
    promptWhenUnsaved,
    getModalMessage: (affectedTabKey) =>
      getModalMessage(affectedTabKey, editedTabs, tabsConfig, __),
    clearEditedTab,
  });

  const getLeaveWarningMessage = (unsavedTabsKeys, location) => {
    if (location.pathname === window.location.pathname) {
      return "";
    }
    return getModalMessage(null, editedTabs, tabsConfig, __);
  };

  return (
    <>
      <Tabs
        animated={animated}
        activeKey={displayedKey}
        renderTabBar={() => <DefaultHashTabBar />}
        renderTabContent={() => <TabContent animated={animated} />}
      >
        {filterTabsByAcl(tabsConfig, userInfo).map(
          ({
            name,
            key,
            component,
            disabled,
            tabClassName = null,
            tabTitle = null,
            display = true,
            link,
          }) =>
            display ? (
              <TabPane
                tab={
                  <TabComponent
                    name={name}
                    key={key}
                    tabClassName={tabClassName}
                    edited={editedTabs[key]}
                    link={link}
                  />
                }
                key={key}
                disabled={Boolean(disabled)}
              >
                {cloneElement(component, {
                  active: displayedKey === key,
                  setIsEdited: (state, cb) => {
                    setTabIsEdited(key, state, cb);
                  },
                })}
              </TabPane>
            ) : null
        )}
      </Tabs>
      {promptWhenUnsaved ? (
        <Prompt
          when={unsavedForms.length > 0}
          message={(location) => getLeaveWarningMessage(unsavedForms, location)}
        />
      ) : null}
      <UnsavedChangesModal
        showModal={showModal}
        handleCancelLeave={handleCancelLeave}
        handleConfirmLeave={handleConfirmLeave}
        message={modalMessage}
      />
    </>
  );
}

TabsWithMemory.propTypes = {
  activeKey: PropTypes.string,
  animated: PropTypes.bool,
  defaultActiveKey: PropTypes.string.isRequired,
  promptWhenUnsaved: PropTypes.bool,
  tabsConfig: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      key: PropTypes.string,
      aclKey: PropTypes.string,
      component: PropTypes.node,
      disabled: PropTypes.bool,
      link: PropTypes.string,
    })
  ).isRequired,
};

TabsWithMemory.defaultProps = {
  activeKey: "",
  animated: false,
  promptWhenUnsaved: true,
};

const TabComponent = ({ name, className, title, edited, link }) =>
  link ? (
    <RedirectTab name={name} link={link} key="tab-redirect-component" />
  ) : (
    <span className={className} title={title} key="tab-component">
      <span key="tab-name">{name}</span>
      {edited && (
        <span key="edit-icon-wrapper" className="ml-2">
          <MdModeEdit
            style={{ position: "absolute", top: "8px" }}
            fontSize="15px"
            key="edit-icon"
          />
        </span>
      )}
    </span>
  );

const RedirectTab = ({ name, link }) => {
  const handleClick = () => {
    window.open(link, "_blank", "noopener,noreferrer");
  };
  return (
    <span
      role="button"
      tabIndex="0"
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          handleClick();
        }
      }}
    >
      {name}
    </span>
  );
};

TabComponent.propTypes = {
  name: string.isRequired,
  className: string.isRequired,
  title: string.isRequired,
  edited: bool,
  link: string.isRequired,
};

TabComponent.defaultProps = {
  edited: false,
};

RedirectTab.propTypes = {
  name: string.isRequired,
  link: string.isRequired,
};
